import {
    useImperativeHandle,
    forwardRef,
    useEffect,
    Fragment,
    useState,
    useRef,
    useId
} from "react";
import {
    ScrollView,
    Animated,
    Platform,
    View
} from "react-native";
import type IDialogProps from "./type";
import type {
    IDialogRef
} from "./type";
import stylesheet, {
    getDialogCloseIconColor,
    DIALOG_TYPE_ROLES,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    RefForwardingComponent,
    NCoreUIKitIcon
} from "../../types";
import {
    MOTION_SURFACE_SCALE,
    useReducedMotion
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import type {
    IModalRef
} from "../modal/type";
import {
    X as XIcon
} from "lucide-react-native";
import {
    getModalCloseStyle,
    getModalMotion
} from "../modal/stylesheet";
import {
    getStateColorKey
} from "../../helpers/theme/state";
import {
    warnOnce
} from "../../utils/development";
import {
    uuid
} from "../../utils";
import AnimatedIcon from "../animatedIcon";
import Button from "../button";
import Modal from "../modal";
import Text from "../text";

const DialogCloseIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    animation="signature"
    color={color}
    icon={XIcon}
    size={size}
/>;

const Dialog: RefForwardingComponent<IDialogRef, IDialogProps> = ({
    bottomContentContainerStyle,
    contentJustify = "centered",
    contentContainerStyle,
    bottomContainerStyle,
    headerContainerStyle,
    secondaryButtonProps,
    secondaryButtonStyle,
    closeAnimationDelay,
    isOpen: isOpenProp,
    isShowClose = true,
    openAnimationDelay,
    primaryButtonProps,
    primaryButtonStyle,
    type = "neutral",
    withModal = true,
    bottomComponent,
    headerComponent,
    size = "medium",
    closeIconProps,
    customLocalize,
    icon: IconProp,
    onOverlayPress,
    variant = "ok",
    onOpenChange,
    customTheme,
    modalProps,
    id: outID,
    isVisible,
    children,
    onClosed,
    content,
    style,
    title
}, ref) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        typography,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const isReduceMotionEnabled = useReducedMotion();

    const baseId = useId();

    const id = useRef(outID ? outID : uuid());

    const isOpen = isOpenProp ?? isVisible ?? false;

    const modalRef = useRef<IModalRef>(null);

    const bareOpacityAnim = useRef(new Animated.Value(withModal || isOpen ? 1 : 0)).current;

    const bareScaleAnim = useRef(new Animated.Value(withModal || isOpen ? 1 : MOTION_SURFACE_SCALE)).current;

    const [
        isRendered,
        setIsRendered
    ] = useState(isOpen);

    const isMountedRef = useRef(false);

    const isExitedRef = useRef(!isOpen);

    const closeCallbacks = useRef<Array<(props: {
        id: string;
    }) => void>>([]);

    const isClosingRef = useRef(false);

    const onClosedRef = useRef(onClosed);

    const onOpenChangeRef = useRef(onOpenChange);

    const titleId = `${baseId}-title`;

    const descriptionId = `${baseId}-description`;

    const bodyId = `${baseId}-body`;

    const isLoading = primaryButtonProps?.isLoading === true;

    const isInfoWithContent = variant === "info" && !!children;

    const isCentered = contentJustify === "centered" && !isInfoWithContent;

    const isTitleRendered = !headerComponent && !!title;

    const isDescriptionRendered = !headerComponent && !!content;

    const describedById = isDescriptionRendered ? descriptionId : children ? bodyId : undefined;

    const currentTypeRoles = DIALOG_TYPE_ROLES[type];

    const isNativeDriver = Platform.OS !== "web";

    const currentMotion = getModalMotion({
        isReduceMotionEnabled,
        closeAnimationDelay,
        openAnimationDelay,
        durations
    });

    const currentCloseStyle = getModalCloseStyle({
        controlHeights,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        radiuses,
        borders,
        spaces
    });

    const {
        bottomContentContainer: bottomContentContainerDynamicStyle,
        languageHeaderTexts: languageHeaderTextsDynamicStyle,
        bottomContainer: bottomContainerDynamicStyle,
        headerContainer: headerContainerDynamicStyle,
        centeredHeader: centeredHeaderDynamicStyle,
        languageHeader: languageHeaderDynamicStyle,
        scrollContent: scrollContentDynamicStyle,
        closeIcon: closeIconDynamicStyle,
        container: containerDynamicStyle,
        iconTile: iconTileDynamicStyle,
        gutter: gutterDynamicStyle
    } = useStyles({
        contentJustify: isCentered ? "centered" : "language-based",
        alignContent: modalProps?.alignContent ?? "center",
        closeStyle: currentCloseStyle,
        controlHeights,
        shapeConfigs,
        isShowClose,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        variant,
        colors,
        isOpen,
        spaces,
        size,
        type
    });

    useEffect(() => {
        onClosedRef.current = onClosed;

        onOpenChangeRef.current = onOpenChange;
    });

    useEffect(() => {
        if(isVisible === undefined) {
            return;
        }

        warnOnce({
            message: "Dialog: \"isVisible\" is deprecated and will be removed in the next major version. Use \"isOpen\" instead."
        });
    }, [
        isVisible
    ]);

    useEffect(() => {
        const isFirstRun = !isMountedRef.current;

        isMountedRef.current = true;

        if(withModal) {
            return;
        }

        if(!isOpen) {
            if(!isExitedRef.current) {
                return;
            }

            bareOpacityAnim.setValue(0);

            bareScaleAnim.setValue(MOTION_SURFACE_SCALE);

            return;
        }

        if(isFirstRun) {
            return;
        }

        Animated.parallel([
            Animated.timing(bareOpacityAnim, {
                duration: currentMotion.openFadeDuration,
                easing: currentMotion.openEasing,
                useNativeDriver: isNativeDriver,
                toValue: 1
            }),
            Animated.timing(bareScaleAnim, {
                duration: currentMotion.openMoveDuration,
                easing: currentMotion.openEasing,
                useNativeDriver: isNativeDriver,
                toValue: 1
            })
        ]).start();
    }, [
        isOpen
    ]);

    useEffect(() => {
        if(isOpen || !isClosingRef.current) {
            return;
        }

        isClosingRef.current = false;

        flushCloseCallbacks();
    }, [
        isOpen
    ]);

    useEffect(() => {
        if(isOpen) {
            isExitedRef.current = false;

            setIsRendered(true);

            return;
        }

        if(!isRendered) {
            return;
        }

        if(isExitedRef.current) {
            setIsRendered(false);

            return;
        }

        closeAnimation(() => {
            setIsRendered(false);
        });
    }, [
        isOpen
    ]);

    useEffect(() => {
        return () => {
            bareOpacityAnim.stopAnimation();

            bareScaleAnim.stopAnimation();
        };
    }, []);

    const flushCloseCallbacks = () => {
        closeCallbacks.current.splice(0).forEach((callback) => {
            callback({
                id: id.current
            });
        });
    };

    const finishClose = () => {
        isClosingRef.current = false;

        isExitedRef.current = true;

        if(!withModal) {
            bareScaleAnim.setValue(MOTION_SURFACE_SCALE);
        }

        flushCloseCallbacks();

        if(onClosedRef.current) onClosedRef.current({
            id: id.current
        });
    };

    const closeAnimation = (_onClosed?: (props: {
        id: string;
    }) => void) => {
        if(_onClosed) {
            closeCallbacks.current.push(_onClosed);
        }

        if(isClosingRef.current) {
            return;
        }

        isClosingRef.current = true;

        if(withModal && modalRef.current) {
            modalRef.current.closeAnimation(finishClose);

            return;
        }

        if(withModal) {
            finishClose();

            return;
        }

        Animated.timing(bareOpacityAnim, {
            duration: currentMotion.closeFadeDuration,
            easing: currentMotion.closeEasing,
            useNativeDriver: isNativeDriver,
            toValue: 0
        }).start(({
            finished
        }) => {
            if(finished) {
                finishClose();
            }
        });
    };

    const requestClose = (_onClosed?: (props: {
        id: string;
    }) => void) => {
        if(!isClosingRef.current && !isExitedRef.current && onOpenChangeRef.current) {
            onOpenChangeRef.current(false);
        }

        closeAnimation(_onClosed);
    };

    useImperativeHandle(
        ref,
        () => ({
            closeAnimation: requestClose
        })
    );

    const renderTitle = () => {
        if(!title) {
            return null;
        }

        return <Text
            style={[
                stylesheet.headerTitle,
                isCentered ? stylesheet.centeredText : null
            ]}
            customTheme={customTheme}
            variant="titleLargeSize"
            role="heading"
            color="high"
            id={titleId}
        >
            {title}
        </Text>;
    };

    const renderIconTile = () => {
        if(!IconProp) {
            return null;
        }

        const stateKey = getStateColorKey({
            type
        });

        return <View
            style={[
                stylesheet.iconTile,
                iconTileDynamicStyle
            ]}
            aria-hidden={true}
        >
            <IconProp
                customColor={colors.system.onContainer[stateKey]}
                size={iconSizes.large}
                color="default"
            />
        </View>;
    };

    const renderDescription = () => {
        if(!content) {
            return null;
        }

        return <Text
            style={isCentered ? stylesheet.centeredText : null}
            customTheme={customTheme}
            variant="bodyMediumSize"
            id={descriptionId}
            color="mid"
        >
            {content}
        </Text>;
    };

    const renderHeaderTexts = () => {
        const iconTile = renderIconTile();

        if(!iconTile) {
            return <Fragment>
                {renderTitle()}
                {renderDescription()}
            </Fragment>;
        }

        if(isCentered) {
            return <View
                style={[
                    stylesheet.centeredHeader,
                    centeredHeaderDynamicStyle
                ]}
            >
                {iconTile}
                {renderTitle()}
                {renderDescription()}
            </View>;
        }

        return <View
            style={[
                stylesheet.languageHeader,
                languageHeaderDynamicStyle
            ]}
        >
            {iconTile}
            <View
                style={[
                    stylesheet.languageHeaderTexts,
                    languageHeaderTextsDynamicStyle
                ]}
            >
                {renderTitle()}
                {renderDescription()}
            </View>
        </View>;
    };

    const renderHeader = () => {
        if(!headerComponent && !title && !content) {
            return null;
        }

        return <View
            style={[
                headerContainerStyle,
                stylesheet.headerContainer,
                headerContainerDynamicStyle
            ]}
        >
            {headerComponent || renderHeaderTexts()}
        </View>;
    };

    const renderContent = () => {
        if(!children) {
            return null;
        }

        return <View
            style={[
                contentContainerStyle,
                stylesheet.content,
                isCentered ? stylesheet.centeredContent : null
            ]}
            id={bodyId}
        >
            {children}
        </View>;
    };

    const renderScrollArea = () => {
        const header = renderHeader();

        const body = renderContent();

        if(!header && !body) {
            return null;
        }

        return <ScrollView
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={[
                stylesheet.scrollContent,
                scrollContentDynamicStyle
            ]}
            alwaysBounceVertical={false}
            style={stylesheet.scroll}
        >
            {header}
            {body}
        </ScrollView>;
    };

    const renderSecondaryButton = () => {
        if(variant !== "yes-no") {
            return null;
        }

        return <Button
            customTheme={customTheme}
            variant="outline"
            type="neutral"
            {...secondaryButtonProps}
            onPress={() => {
                if(secondaryButtonProps?.onPress) secondaryButtonProps.onPress({
                    closeAnimation: (_onClosed?: (props: {
                        id: string;
                    }) => void) => {
                        requestClose(_onClosed);
                    }
                });
            }}
            isDisabled={isLoading || secondaryButtonProps?.isDisabled}
            title={secondaryButtonProps?.title || localize("cancel")}
            spreadBehaviour={isCentered ? "stretch" : "free"}
            style={secondaryButtonStyle}
        />;
    };

    const renderPrimaryButton = () => {
        return <Button
            type={currentTypeRoles.primaryButtonType}
            customTheme={customTheme}
            size="medium"
            {...primaryButtonProps}
            onPress={() => {
                if(primaryButtonProps?.onPress) primaryButtonProps.onPress({
                    closeAnimation: (_onClosed?: (props: {
                        id: string;
                    }) => void) => {
                        requestClose(_onClosed);
                    }
                });
            }}
            title={primaryButtonProps?.title || localize("dialog-ok")}
            spreadBehaviour={isCentered ? "stretch" : "free"}
            style={primaryButtonStyle}
            variant="filled"
        />;
    };

    const renderBottom = () => {
        if(variant === "info") {
            return null;
        }

        return <View
            style={[
                bottomContainerStyle,
                stylesheet.bottomContainer,
                bottomContainerDynamicStyle
            ]}
        >
            {bottomComponent || <View
                style={[
                    bottomContentContainerStyle,
                    stylesheet.bottomContentContainer,
                    bottomContentContainerDynamicStyle
                ]}
            >
                {renderSecondaryButton()}
                {renderPrimaryButton()}
            </View>}
        </View>;
    };

    const renderCloseIcon = () => {
        if(!isShowClose) {
            return null;
        }

        return <Button
            borderRadius={currentCloseStyle.borderRadius}
            customIconColor={getDialogCloseIconColor({
                closeIconProps,
                colors
            })}
            accessibilityLabel={localize("close")}
            iconSize={closeIconProps?.size}
            onPress={() => requestClose()}
            style={[
                stylesheet.closeIcon,
                closeIconDynamicStyle
            ]}
            customTheme={customTheme}
            spreadBehaviour="free"
            icon={DialogCloseIcon}
            isDisabled={isLoading}
            variant="ghost"
            type="neutral"
            size="small"
        />;
    };

    const renderSurface = () => {
        const bareAccessibilityProps = withModal ? {} : {
            "aria-labelledby": isTitleRendered ? titleId : undefined,
            role: currentTypeRoles.role
        };

        const bareWebProps = !withModal && Platform.OS === "web" ? {
            "aria-describedby": describedById
        } : {};

        return <Animated.View
            {...bareAccessibilityProps}
            {...bareWebProps}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle,
                {
                    transform: withModal ? undefined : [
                        {
                            scale: bareScaleAnim
                        }
                    ],
                    opacity: bareOpacityAnim
                }
            ]}
            aria-busy={isLoading}
        >
            {renderScrollArea()}
            {renderBottom()}
            {renderCloseIcon()}
        </Animated.View>;
    };

    if(!isRendered && !isOpen) {
        return null;
    }

    if(!withModal) {
        return renderSurface();
    }

    return <Modal
        {...modalProps}
        aria-labelledby={isTitleRendered ? titleId : modalProps?.["aria-labelledby"]}
        closeAnimationDelay={closeAnimationDelay ?? modalProps?.closeAnimationDelay}
        openAnimationDelay={openAnimationDelay ?? modalProps?.openAnimationDelay}
        aria-describedby={describedById ?? modalProps?.["aria-describedby"]}
        isDisabledOverlay={isLoading || modalProps?.isDisabledOverlay}
        customLocalize={modalProps?.customLocalize ?? customLocalize}
        customTheme={modalProps?.customTheme ?? customTheme}
        onOverlayPress={onOverlayPress ? () => {
            onOverlayPress({
                closeAnimation: (_onClosed?: (props: {
                    id: string;
                }) => void) => {
                    requestClose(_onClosed);
                }
            });
        } : undefined}
        role={currentTypeRoles.role}
        id={`${id.current}-modal`}
        isShowSurface={false}
        isShowClose={false}
        ref={modalRef}
    >
        <View
            style={[
                stylesheet.gutter,
                gutterDynamicStyle
            ]}
        >
            {renderSurface()}
        </View>
    </Modal>;
};
export default forwardRef(Dialog);
